/**
 * Intercom Integration Service for Credibila
 * Provides Fin AI chat support for investors
 */

// Environment configuration
const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID
const IS_INTERCOM_ENABLED = process.env.REACT_APP_INTERCOM_ENABLED === 'true'

// Types for Intercom user data
interface IntercomUser {
  user_id?: string | number
  email?: string
  name?: string
  created_at?: number
  // Custom attributes for Credibila
  kyc_status?: string
  account_status?: string
  current_route?: string
  investor_role?: string
  phone?: string
  last_investment_date?: string
  // Timestamp fields
  kyc_updated_at?: string
  account_updated_at?: string
  support_issue?: string
  support_context?: string
  support_requested_at?: string
  // Flexible for additional attributes
  [key: string]: any
}

// Global window interface extension
declare global {
  interface Window {
    // Intercom may not be injected yet in the page, so make it optional
    Intercom?: ((command: string, ...args: any[]) => void) | undefined
  }
}

/**
 * Inject Intercom script into the page
 */
function injectIntercomScript(): void {
  // Prevent multiple script injections
  if (window.Intercom || !INTERCOM_APP_ID) {
    return
  }

  // Skip in development unless explicitly enabled
  if (!IS_INTERCOM_ENABLED) {
    return
  }

  // Intercom installation snippet
  ;(function () {
    const w = window as any
    const ic = (w.Intercom = function (...args: any[]) {
      ;(ic as any).c(args)
    })
    ;(ic as any).q = []
    ;(ic as any).c = function (args: any) {
      ;(ic as any).q.push(args)
    }
    const d = document
    const s = d.createElement('script')
    s.type = 'text/javascript'
    s.async = true
    s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
    const m = d.getElementsByTagName('script')[0]
    if (m && m.parentNode) {
      m.parentNode.insertBefore(s, m)
    }
  })()
}

/**
 * Boot Intercom with user data
 */
export function bootIntercom(user: IntercomUser): void {
  if (!INTERCOM_APP_ID) {
    console.warn('Intercom app_id not configured')
    return
  }

  // Booting Intercom with provided user context

  // Inject script if not already done
  injectIntercomScript()

  // Wait for Intercom to be available
  const initIntercom = () => {
    if (window.Intercom) {
      try {
        const bootData = {
          app_id: INTERCOM_APP_ID,
          user_id: user.user_id,
          email: user.email,
          name: user.name,
          created_at: user.created_at || Math.floor(Date.now() / 1000),
          // Custom attributes for support context
          kyc_status: user.kyc_status,
          account_status: user.account_status,
          current_route: user.current_route || window.location.pathname,
          investor_role: user.investor_role,
          phone: user.phone,
          last_investment_date: user.last_investment_date,
          // Show launcher by default
          hide_default_launcher: false,
        }

        window.Intercom('boot', bootData)
        // Ensure messenger stays closed on page load; users open it manually via launcher
        setTimeout(() => {
          if (window.Intercom) window.Intercom('hide')
        }, 200)
      } catch (error) {
        console.error('Error booting Intercom:', error)
      }
    } else {
      // Retry if Intercom not loaded yet (max 10 retries)
      if ((initIntercom as any).retryCount < 10) {
        ;(initIntercom as any).retryCount = ((initIntercom as any).retryCount || 0) + 1
        setTimeout(initIntercom, 500)
      } else {
        console.error('Intercom failed to load after 10 retries')
      }
    }
  }

  initIntercom()
}

/**
 * Update Intercom with new user data or context
 */
export function updateIntercom(attributes: Partial<IntercomUser>): void {
  if (window.Intercom) {
    window.Intercom('update', {
      ...attributes,
      current_route: attributes.current_route || window.location.pathname,
    })
  }
}

/**
 * Show the Intercom messenger
 */
export function showIntercom(): void {
  if (window.Intercom) {
    window.Intercom('show')
  }
}

/**
 * Hide the Intercom messenger
 */
export function hideIntercom(): void {
  if (window.Intercom) {
    window.Intercom('hide')
  }
}

/**
 * Shutdown Intercom (call on logout)
 */
export function shutdownIntercom(): void {
  if (window.Intercom) {
    window.Intercom('shutdown')
  }
}

/**
 * Track custom events for support context
 */
export function trackIntercomEvent(eventName: string, metadata?: Record<string, any>): void {
  if (window.Intercom) {
    window.Intercom('trackEvent', eventName, metadata)
  }
}

/**
 * Helper to manage Intercom visibility based on route
 */
export function manageIntercomVisibility(pathname: string): void {
  // Routes where Intercom should be hidden to avoid UI conflicts
  const hiddenRoutes = [
    '/invest/signing-complete',
    '/payment',
    // Add more routes as needed
  ]

  const shouldHide = hiddenRoutes.some((route) => pathname.includes(route))

  if (shouldHide) {
    hideIntercom()
  }
  // Do not auto-show on other routes; let users open manually via launcher
}

/**
 * Track key user actions for support context
 */
export function trackUserAction(action: string, context?: Record<string, any>): void {
  const eventMap: Record<string, string> = {
    kyc_started: 'KYC Process Started',
    kyc_document_uploaded: 'KYC Document Uploaded',
    investment_started: 'Investment Process Started',
    payment_initiated: 'Payment Initiated',
    withdrawal_requested: 'Redemption Requested',
    support_contact: 'Support Contact Initiated',
  }

  const eventName = eventMap[action] || action
  trackIntercomEvent(eventName, {
    timestamp: new Date().toISOString(),
    page: window.location.pathname,
    ...context,
  })
}

// Export default object with all methods
const intercomService = {
  boot: bootIntercom,
  update: updateIntercom,
  show: showIntercom,
  hide: hideIntercom,
  shutdown: shutdownIntercom,
  trackEvent: trackIntercomEvent,
  trackUserAction: trackUserAction,
  manageVisibility: manageIntercomVisibility,
}

export default intercomService
